import { useState } from "react"
import { Link } from "react-router-dom"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Layout } from "../components/Layout"
import { Button } from "../components/Button"
import { ConfirmDialog } from "../components/Dialog/ConfirmDialog"
import { client, APIError } from "../api"
import type { previewBackfill } from "../../../backend/src/lib/team-ownership-backfill"

type Preview = Awaited<ReturnType<typeof previewBackfill>>
type Kind = keyof Preview["totals"]
const endpoint = client.admin["team-ownership-backfill"]
const KINDS: { kind: Kind; plural: string }[] = [
  { kind: "playbook", plural: "Playbooks" },
  { kind: "call_sheet", plural: "Call sheets" },
  { kind: "wristband", plural: "Wristbands" },
]

function message(error: unknown) {
  return error instanceof APIError
    ? error.originalError.message
    : error instanceof Error
      ? error.message
      : "Request failed"
}

export function TeamOwnershipBackfill() {
  const [preview, setPreview] = useState<Preview | null>(null)
  const [confirm, setConfirm] = useState(false)
  const queryClient = useQueryClient()
  const prepare = useMutation({
    mutationFn: async () => {
      const response = await endpoint.preview.$post({ json: {} })
      return response.json()
    },
    onMutate: () => setPreview(null),
    onSuccess: setPreview,
  })
  const apply = useMutation({
    mutationFn: async () => {
      if (!preview) throw new Error("Check what would move first")
      const response = await endpoint.apply.$post({
        json: { token: preview.token, confirm: true },
      })
      return response.json()
    },
    onSuccess: () => {
      setPreview(null)
      void queryClient.invalidateQueries({ queryKey: ["admin"] })
    },
  })
  const busy = prepare.isPending || apply.isPending
  const moving = preview?.totals
    ? KINDS.reduce((n, { kind }) => n + preview.totals[kind].assign, 0)
    : 0

  return (
    <Layout>
      <div className="max-w-4xl space-y-6">
        <Link to="/diagrams" className="text-primary underline">
          Back to diagrams
        </Link>
        <div>
          <h1 className="text-2xl font-bold">Put playbooks on their team</h1>
          <p className="text-secondary">
            Moves personal playbooks, call sheets and wristbands onto the team that owns all of
            their plays, when the person who made them is still a coach or admin there. No play
            moves, and nothing already on a team changes. Everything else stays personal, listed
            below with the reason. Running it twice changes nothing the second time.
          </p>
        </div>
        <Button
          disabled={busy}
          onClick={() => {
            apply.reset()
            prepare.mutate()
          }}
        >
          {prepare.isPending ? "Checking…" : "Check what would move"}
        </Button>

        {preview && (
          <div className="space-y-4">
            <div className="border rounded p-4 space-y-3">
              <h2 className="font-semibold">What would happen</h2>
              <table className="text-sm">
                <thead>
                  <tr className="text-left text-secondary">
                    <th className="pr-8 py-1 font-medium"></th>
                    <th className="pr-8 py-1 font-medium">Move to their team</th>
                    <th className="py-1 font-medium">Stay personal</th>
                  </tr>
                </thead>
                <tbody>
                  {KINDS.map(({ kind, plural }) => (
                    <tr key={kind}>
                      <td className="pr-8 py-1">{plural}</td>
                      <td className="pr-8 py-1">{preview.totals[kind].assign}</td>
                      <td className="py-1">{preview.totals[kind].stay}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {KINDS.map(({ kind, plural }) => (
              <div key={kind} className="border rounded p-4 space-y-2">
                <h2 className="font-semibold">{plural}</h2>
                {preview.groups
                  .filter((g) => g.kind === kind && g.count > 0)
                  .map((g) => (
                    <details key={`${g.outcome}-${g.reason}`}>
                      <summary className="cursor-pointer">
                        {g.outcome === "assign" ? "Move to their team" : g.label}: {g.count}
                      </summary>
                      <p className="font-mono text-xs break-words py-2">{g.public_ids.join(" ")}</p>
                    </details>
                  ))}
                {preview.totals[kind].assign + preview.totals[kind].stay === 0 && (
                  <p className="text-secondary">No personal {plural.toLowerCase()}.</p>
                )}
              </div>
            ))}
            {moving > 0 ? (
              <Button disabled={busy} onClick={() => setConfirm(true)}>
                Move {moving} {moving === 1 ? "item" : "items"} to their teams
              </Button>
            ) : (
              <p role="status">Nothing left to move.</p>
            )}
          </div>
        )}

        {(prepare.error || apply.error) && (
          <p role="alert">{message(prepare.error || apply.error)} Check again before retrying.</p>
        )}
        {apply.data && (
          <div role="status" className="border rounded p-4 space-y-1">
            <p>
              Moved {apply.data.applied.playbook} playbooks, {apply.data.applied.call_sheet} call
              sheets and {apply.data.applied.wristband} wristbands to their teams.
            </p>
            {apply.data.skipped > 0 && (
              <p>
                {apply.data.skipped} changed since the preview and were left alone:{" "}
                <span className="font-mono text-xs">{apply.data.skipped_ids.join(" ")}</span>
              </p>
            )}
            {apply.data.new_since_preview > 0 && (
              <p>{apply.data.new_since_preview} more now qualify. Check again to include them.</p>
            )}
            {apply.data.operation_public_id && (
              <p>Audit reference: {apply.data.operation_public_id}</p>
            )}
          </div>
        )}
        <ConfirmDialog
          open={confirm}
          onOpenChange={setConfirm}
          title="Put these on their teams?"
          description={`Move ${preview?.totals.playbook.assign ?? 0} playbooks, ${preview?.totals.call_sheet.assign ?? 0} call sheets and ${preview?.totals.wristband.assign ?? 0} wristbands onto their teams? Every member of the team will be able to see the playbooks, and only coaches and admins will be able to edit them. Plays do not move. Anything that changed since the preview is skipped.`}
          confirmLabel="Confirm move"
          onConfirm={() => apply.mutate()}
        />
      </div>
    </Layout>
  )
}
